import { useState } from "react";
import { Shield, AlertTriangle, Home, Scale } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import SiteNav from "../components/Layout/SiteNav";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      <div className="container mx-auto max-w-4xl px-4 py-12">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold">
            Privacy Policy & Disclaimer
          </h1>
          <p className="text-muted-foreground">
            Learn how we handle your information and our legal disclaimers
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Last updated: March 4, 2026
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="privacy" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="privacy" className="gap-2">
              <Shield className="h-4 w-4" />
              Privacy Policy
            </TabsTrigger>
            <TabsTrigger value="disclaimer" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Disclaimer
            </TabsTrigger>
          </TabsList>

          {/* Privacy Policy Tab */}
          <TabsContent value="privacy">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">
                  Muslim+ Privacy Policy
                </CardTitle>
                <CardDescription>
                  This Privacy Policy explains how Muslim+ ("we," "us," or "our")
                  collects, uses, and protects your information when you use our
                  platform and services. We are committed to protecting your
                  privacy and providing a safe user experience.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    1. Information We Collect
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    The information we collect depends on how you use Muslim+.
                    For community members, we collect your name, email address,
                    and phone number during account registration. For masjid
                    administrators, we additionally collect masjid details and
                    administrative contact information. For halal business
                    subscribers, we collect business information (name, address,
                    phone, website), halal certification documents, and payment
                    method details processed through Stripe. We also collect
                    authentication data when you sign in via email/password or
                    Google OAuth.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    2. How We Use Your Information
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    We use the information we collect to: create and manage your
                    account; provide, maintain, and improve our services;
                    process halal business applications and subscriptions;
                    process payments for subscription services; display business
                    listings in our halal directory; communicate with you about
                    your account, services, or changes to our terms; verify
                    halal certification submissions; and comply with legal
                    obligations. We do not sell your personal information to
                    third parties.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    3. Location Permissions
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    If you choose to enable location access, the app uses your
                    location solely to display nearby masajid, prayer times, and
                    halal businesses relevant to your area. This location data
                    is processed on your device and is not stored on our
                    servers.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    4. Notification Permissions
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    If you enable notifications, they are used only to remind
                    you of upcoming prayer times or events. Notification tokens
                    may be stored to deliver these notifications to your device.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    5. Third-Party Services
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Muslim+ uses the following third-party services: Firebase
                    Authentication by Google for secure account management;
                    Stripe, Inc. for payment processing (Stripe's privacy policy
                    is available at stripe.com/privacy); Google Places API for
                    address autocomplete during business registration; and
                    prayer time APIs for accurate prayer schedules. Each
                    third-party service has its own privacy policy governing the
                    data they process. We encourage you to review their policies.
                    We share only the minimum data necessary with each service.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    6. Data Storage & Security
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Your data is stored securely using Google Cloud
                    infrastructure with encryption at rest and in transit. Halal
                    certification documents are stored in secure cloud storage
                    with restricted access. Payment information is processed and
                    stored by Stripe and is never stored on our servers. We
                    implement industry-standard security measures to protect
                    your data, but no method of electronic storage is 100%
                    secure.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    7. Data Retention
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    We retain your account data for as long as your account is
                    active. For halal business subscribers, we retain business
                    and certification data for the duration of your subscription
                    plus 90 days after cancellation. Halal certification
                    documents are deleted within 90 days of account closure or
                    application rejection. You may request deletion of your data
                    at any time by contacting us.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    8. Your Rights
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Depending on your location, you may have the right to:
                    access the personal data we hold about you; request
                    correction of inaccurate data; request deletion of your
                    data; object to or restrict certain processing of your data;
                    request portability of your data; and opt out of marketing
                    communications. To exercise any of these rights, please
                    contact us at support@muslimplus.org. We will respond to
                    your request within 30 days.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    9. Children's Privacy
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    The Service is not directed to individuals under 13 years of
                    age, and we do not knowingly collect personal information
                    from children under 13. If we become aware that a child
                    under 13 has provided us with personal information, we will
                    take steps to delete that information.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    10. Changes to This Policy
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    This privacy policy may be updated to reflect changes in our
                    practices or legal requirements. We will notify users of
                    material changes by posting the updated policy on our
                    website and, for active subscribers, by email. Any updates
                    will be reflected with a new "Last updated" date at the top
                    of this page.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    11. Contact Us
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    If you have any questions or concerns about this policy or
                    our data practices, feel free to reach out to us at{" "}
                    <a
                      href="mailto:support@muslimplus.org"
                      className="text-primary hover:underline"
                    >
                      support@muslimplus.org
                    </a>
                    .
                  </p>
                </div>

                <Separator />

                <p className="leading-relaxed text-muted-foreground">
                  Thank you for using Muslim+.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disclaimer Tab */}
          <TabsContent value="disclaimer">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Disclaimer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  We make reasonable effort to verify all information is
                  accurate in Muslim+. If you find any discrepancies, please
                  send us your feedback and we will do our best to fix it
                  immediately insha'Allah. May Allah extend the rewards to you
                  for anyone who benefits. Jazakam Allahu Khairan.
                </p>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    Halal Certification Disclaimer
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Muslim+ displays halal certifications as provided by business
                    owners. While we review submitted documentation, Muslim+
                    does not independently verify or guarantee the halal status
                    of any business listed on our platform. Users should
                    exercise their own judgment and due diligence when making
                    dining or purchasing decisions. Muslim+ is not liable for
                    any inaccuracies in halal certification claims made by
                    listed businesses.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    Prayer Times Disclaimer
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Prayer times displayed on Muslim+ are calculated using
                    third-party APIs and astronomical data. While we strive for
                    accuracy, times may vary slightly from those announced at
                    your local masjid. We recommend confirming prayer times
                    with your local masjid for the most accurate information.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer CTA */}
        <div className="mt-12 border-t pt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button variant="outline" size="lg" onClick={() => navigate("/terms")}>
            <Scale className="mr-2 h-4 w-4" />
            Terms of Service
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate("/")}>
            <Home className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
